import { param } from "jquery";
import requests, { setToken } from "./httpServices";
import { Loader } from "react-feather";

const QuizServices = {
  // Helper function to check if a video has a quiz
  // Returns true if video has quizzes, false if no quiz
  hasQuiz: (video) => {
    if (!video) return false;
    return video.hasQuiz !== false && (video.quizzes?.length > 0 || video.quizCount > 0);
  },

  // Helper function to check if quiz retake is allowed
  // Note: is_quiz_retake flag is only set for role 2 (school teachers)
  // Other roles should not see retake option
  canRetakeQuiz: (response, role) => {
    const roleNum = Number(role);
    // Only role 2 (school teachers) can retake quizzes
    if (roleNum !== 2) {
      return false;
    }
    // For role 2, check the is_quiz_retake flag
    return response?.is_quiz_retake === true;
  },

  // Helper function to get the user ID from quiz process response
  // Note: Either userId or schoolId is set, depending on role
  // Role 2 (teacher) uses userId, Role 1 (school admin) uses schoolId
  getProcessUserId: (response, role) => {
    const roleNum = Number(role);
    if (roleNum === 2) {
      return response?.userId;
    } else if (roleNum === 1) {
      return response?.schoolId;
    }
    return null;
  },

  // Helper function to filter videos that have quizzes
  // Useful for displaying only videos with quizzes
  getVideosWithQuizzes: (videos) => {
    if (!Array.isArray(videos)) return [];
    return videos.filter(video => QuizServices.hasQuiz(video));
  },

  // Helper function to filter videos that don't have quizzes
  // Useful for displaying videos without quizzes (like Course 8)
  getVideosWithoutQuizzes: (videos) => {
    if (!Array.isArray(videos)) return [];
    return videos.filter(video => !QuizServices.hasQuiz(video));
  },

  completeProcess: async (teacher_id = null, courseName = null, quizId = null) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const userId = teacher_id || JSON.parse(localStorage.getItem("user"))._id;

      // Validate required parameters
      if (!quizId) {
        console.error("❌ Quiz completion failed: quizId is required");
        throw new Error("Quiz ID is required to complete the quiz. Please refresh and try again.");
      }

      const params = {
        role: teacher_id ? 2 : 1,
        userId,
        courseName,  // ✅ ADDED: Pass course name to backend
        quizId,      // ✅ ADDED: Pass quiz ID to backend
      };

      console.log("✅ Submitting quiz completion:", params);
      const response = await requests.post("/quiz/complete", params);
      console.log("✅ Quiz completion successful:", response);
      return response;
    } catch (error) {
      console.error("❌ Error in quiz complete api:", error);
      throw error;
    }
  },

  saveProcess: async (
    currentGroupIndex,
    currentVideoIndex,
    teacherId = null
  ) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const userId = teacherId || JSON.parse(localStorage.getItem("user"))._id;

      const params = {
        role: teacherId ? 2 : 1,
        userId,
        currentGroupIndex,
        currentVideoIndex,
      };

      const response = await requests.put("/quiz/save-process", params);
      return response;
    } catch (error) {
      console.error("Error in quiz complete api:", error);
      throw error;
    }
  },

  checkProcess: async (teacherId = null) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const userId = teacherId || JSON.parse(localStorage.getItem("user"))._id;
      const role = Number(localStorage.getItem("userrole"));

      const params = { role, userId };
      const response = await requests.post("/quiz/check-process", params);

      // Validate response - either userId or schoolId should be set, not both
      if (!response._id) {
        throw new Error("Invalid quiz process response: missing _id");
      }

      // Log which field is populated for debugging
      console.log("Quiz process response:", {
        hasUserId: !!response.userId,
        hasSchoolId: !!response.schoolId,
        role,
        currentGroupIndex: response.currentGroupIndex,
        currentVideoIndex: response.currentVideoIndex
      });

      return response;
    } catch (error) {
      console.error("Error in Getting Quiz Process:", error);
      throw error;
    }
  },

  getProcess: async (teacherId) => {
    try {
      const token = localStorage.getItem("token"); // Get token 0from localStorage
      setToken(token);
      const response = await requests.get("/quiz/get-process", {
        params: { teacherId },
      });
      return response;
    } catch (error) {
      console.error("Error in Getting Quiz Process:", error);
      throw error;
    }
  },

  // Get quiz completion status
  getQuizStatus: async (quizId) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);

      if (!quizId) {
        throw new Error("Quiz ID is required");
      }

      console.log(`[getQuizStatus] Fetching status for quiz ${quizId}`);

      const response = await requests.get(`/quiz/status/${quizId}`);

      console.log(`[getQuizStatus] Status retrieved:`, response);

      return response?.status || response;
    } catch (error) {
      console.error("Error getting quiz status:", error);
      throw error;
    }
  },
};

export default QuizServices;
